import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify'
import {
  GridColumnVisibilityModel,
  GridDensity,
  gridFilteredSortedRowIdsSelector,
  GridPreferencePanelsValue,
  GridRowId,
  Toolbar,
  useGridApiContext,
} from '@mui/x-data-grid-premium'
import { useActiveUser } from 'api/slack/useActiveUser'
import ChangeBoardView from 'components/DataGrid/ChangeBoardView'
import CustomFiltersModal from 'components/DataGrid/components/CustomFiltersModal'
import CustomStatusFilter from 'components/DataGrid/createColumns/CustomStatusFilter'
import { ncoStatusFilters } from 'components/DataGrid/filterArrays'
import CustomFiltersMenu from 'components/ui/custom-filters-menu-button'
import ExportCsvButton from 'components/ui/export-csv-button'
import FilterMyDataButton from 'components/ui/filter-my-data-button'
import OpenColumnsMenu from 'components/ui/open-columns-menu'
import OpenFiltersMenu from 'components/ui/open-filters-menu'
import ResetFiltersButton from 'components/ui/reset-filters-button'
import ResetSortingButton from 'components/ui/reset-sorting-button'
import React, { useEffect, useMemo, useState } from 'react'
import EntityAvatar from 'theme/components/Avatar/EntityAvatar'
import { MtbGridToolbarProps } from 'types'
import { useChangeView } from 'utils/useChangeView'

import { Badge, Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import CustomDensityMenu from 'components/DataGrid/components/CustomDensityModal'
import ResetColumnWidths from 'components/ui/reset-column-widths-button'
import ResetGridColumns from 'components/ui/reset-grid-columns'
import DensityMenu from 'components/DataGrid/DensityMenu'
import PivotPanelComponent from 'components/MTB/grid/PivotPanelTrigger'
import SelectMonthRangeFilter from 'components/MTB/grid/SelectMonthRangeFilter'
import { saveModel } from 'components/DataGrid/operators/loadModels'
import QuickFilterComponent from 'components/DataGrid/QuickFilterComponent'
import dayjs from 'dayjs'

const SEO_FILTER_OPTIONS = [
  { label: 'Tier 1', value: 'Tier 1', color: 'rgb(51, 158, 205)' },
  { label: 'Tier 2', value: 'Tier 2', color: 'rgb(121, 175, 253)' },
  { label: 'Tier 3', value: 'Tier 3', color: 'rgb(255, 131, 88)' },
  { label: 'Tier 4', value: 'Tier 4', color: 'rgb(181, 125, 227)' },
]
const isSortModelEqual = (sortModel1, sortModel2) => {
  if (sortModel1?.length !== sortModel2?.length) return false
  return sortModel1?.every((sortItem, index) => {
    const initialSortItem = sortModel2[index]
    return sortItem.field === initialSortItem.field && sortItem.sort === initialSortItem.sort
  })
}

const OptimizationToolbar: React.FC<MtbGridToolbarProps> = ({
  filterModel,
  initialVisibilityState,
  initialSortModel,
  sortModel,
  columnWidths,
  setFilterModel,
  onClearSorting,
  onClearVisibility,
  onChangeVisibility,
  onClearWidths,
  onChangeDensity,
  onClearFilters: _onClearFilters,
  statusLabel: _statusLabel,
  filterOptions,
  context,
  boardTheme,
  onChangeMonth,
  monthFilter,
}) => {
  const theme = useTheme()
  const handleChangeView = useChangeView()
  const [densityAnchorEl, setDensityAnchorEl] = useState(null)
  const [ncoFilterStatus, setNcoFilterStatus] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [expanded, setExpanded] = React.useState(false)
  const [filterStage, setFilterStage] = useState('')
  const [optType, setOptType] = useState('')
  const [anchorEl, setAnchorEl] = useState(null)
  const [viewsAnchorEl, setViewsAnchorEl] = useState(null)
  const [isCustomFiltersOpen, setCustomFiltersOpen] = useState(false)
  const [isCustomViewsOpen, setCustomViewsOpen] = useState(false)
  const { activeUser, isActiveUserLoading, isActiveUserError } = useActiveUser()
  const { profile } = activeUser?.user ?? {}
  const activeUserName =
    activeUser && activeUser.user && activeUser.user.profile ? activeUser.user.profile.real_name : null
  const isSortModelChanged = useMemo(() => {
    return !isSortModelEqual(sortModel, initialSortModel)
  }, [sortModel, initialSortModel])
  // const [selectedView, setSelectedView] = useState(() => {
  //   const savedView = localStorage.getItem(`${context}SelectedView`)
  //   return savedView ? JSON.parse(savedView) : `${context}`
  // })
  const currentMonth = dayjs().format('YYYY-MM')
  const [selectedView, setSelectedView] = useState(() => {
    const savedView = localStorage.getItem(`boardSelectView`)
    return savedView ? JSON.parse(savedView) : `${context}`
  })
  const apiRef = useGridApiContext() // Get DataGrid API

  // 🔹 Toggle Columns Visibility Menu
  const handleOpenColumnsMenu = () => {
    apiRef.current.showPreferences('columns' as GridPreferencePanelsValue)
  }

  // 🔹 Toggle Filter Panel
  const handleOpenFilterPanel = () => {
    apiRef.current.showFilterPanel()
  }

  // 🔹 Export Data
  const handleExport = () => {
    // apiRef.current.exportDataAsCsv()
    const rowIds = gridFilteredSortedRowIdsSelector(apiRef)
    // console.log('rowIds', rowIds)
    const orgOnlyRowIds: GridRowId[] = rowIds.filter(id => {
      // Only include IDs without '::MONTHLY_TASK_' = parent/org rows
      return typeof id === 'string' && !id.includes('::MONTHLY_TASK_') && !id.includes('::PRODUCT_')
    })
    apiRef.current.exportDataAsCsv({
      getRowsToExport: params => orgOnlyRowIds,
    })
  }
  ///////////////////////////////////////////////////////////////////////////\
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      const isTriggerCombo = (isMac ? event.metaKey : event.ctrlKey) && event.key.toLowerCase() === 'f'

      if (isTriggerCombo) {
        event.preventDefault()
        setExpanded(prev => !prev) // 🔁 toggles open/close
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])
  const handleFilterMyData = () => {
    const newModel = {
      ...filterModel,
      quickFilterValues: [activeUserName],
    }
    setFilterModel(newModel)
    saveModel(context, 'FilterModel', newModel)
  }

  const handleSetStatusFilter = (fieldName, selectedValues) => {
    setFilterModel(prevModel => {
      // Remove existing filter for the same field
      let updatedFilters = prevModel.items.filter(item => item.field !== fieldName)

      // If selectedValues is empty, we remove the filter
      if (selectedValues && selectedValues.length > 0) {
        updatedFilters.push({
          id: `${fieldName}-${Date.now()}`, // Ensure unique ID
          field: fieldName,
          operator: 'isAnyOf', // 🔥 Multi-selection operator
          value: selectedValues,
        })
      }

      return {
        ...prevModel,
        items: updatedFilters,
      }
    })
  }
  const handleSetOptType = (fieldName, selectedValues) => {
    setFilterModel(prevModel => {
      // Remove existing filter for the same field
      let updatedFilters = prevModel.items.filter(item => item.field !== fieldName)

      // If selectedValues is empty, we remove the filter
      if (selectedValues && selectedValues.length > 0) {
        updatedFilters.push({
          id: `${fieldName}-${Date.now()}`, // Ensure unique ID
          field: fieldName,
          operator: 'isAnyOf', // 🔥 Multi-selection operator
          value: selectedValues,
        })
      }

      return {
        ...prevModel,
        items: updatedFilters,
      }
    })
  }

  const handleMonthChange = (selectedMonth: string) => {
    const updatedModel = {
      ...filterModel,
      items: [
        ...filterModel.items.filter(item => item.field !== 'dateRange'),
        {
          id: `dateRange-${Date.now()}`,
          field: 'dateRange',
          operator: 'withinMonthRange',
          value: [selectedMonth],
        },
      ],
    }

    setFilterModel(updatedModel)
    saveModel(context, 'FilterModel', updatedModel)

    if (onChangeMonth) {
      onChangeMonth(selectedMonth)
    }
  }
  const handleChangeColumnView = (newVisibilityState: GridColumnVisibilityModel, newVisibilityTitle?: string) => {
    onChangeVisibility(newVisibilityState)
  }
  const handleClearFilters = e => {
    e.preventDefault()
    setFilterModel({
      items: [
        {
          id: `dateRange-${Date.now()}`,
          field: 'dateRange',
          operator: 'withinMonthRange', // ✅ must match the custom operator
          value: [currentMonth], // still needs to be an array
        },
      ],
      quickFilterValues: [],
    })
    setNcoFilterStatus('')
    setFilterStage('')
    setOptType('')
    // onClearFilters()
  }
  const handleOpenCustomFilters = event => {
    setAnchorEl(event.currentTarget) // Attach the popover to the button
    setCustomFiltersOpen(true)
  }
  const handleOpenCustomViews = event => {
    setViewsAnchorEl(event.currentTarget) // Attach the popover to the button
    setCustomViewsOpen(true)
  }
  const handleCloseCustomFilters = () => {
    setAnchorEl(null) // Clear the anchor when closing
    setCustomFiltersOpen(false)
  }
  const handleCloseCustomViews = () => {
    setViewsAnchorEl(null) // Clear the anchor when closing
    setCustomViewsOpen(false)
  }
  const openDensityMenu = event => {
    setDensityAnchorEl(event.currentTarget)
  }
  const closeDensityMenu = () => {
    setDensityAnchorEl(null)
  }
  const changeDensity = (newDensity: GridDensity) => {
    apiRef.current.setDensity(newDensity)
    onChangeDensity(newDensity)
  }

  return (
    <Toolbar>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignContent: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
            ml: 2,
          }}
        >
          <ChangeBoardView onChangeView={newView => handleChangeView({ newView })} />
          <Tooltip title={<Typography sx={{ fontSize: 14 }}>Density</Typography>}>
            <IconButton onClick={openDensityMenu}>
              <FormatAlignJustifyIcon />
            </IconButton>
          </Tooltip>
          <CustomDensityMenu anchorEl={densityAnchorEl} onClose={closeDensityMenu}>
            <DensityMenu changeDensity={changeDensity} />
          </CustomDensityMenu>
          <OpenColumnsMenu onOpenColumns={handleOpenColumnsMenu} />
          <OpenFiltersMenu onOpenFilters={handleOpenFilterPanel} filterModel={filterModel} badgeColor={boardTheme} />
          <ExportCsvButton onExport={handleExport} />
          <PivotPanelComponent />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {/* <SelectMonthFilter filterModel={filterModel} onChangeMonth={handleChangeMonthFilter} /> */}
          <SelectMonthRangeFilter value={monthFilter} onChange={handleMonthChange} />
          {/* <MonthFilterDropdown monthFilter={monthFilter} onChangeMonth={handleChangeMonthFilter} /> */}
          <CustomFiltersMenu
            onOpenCustomFilters={handleOpenCustomFilters}
            count={filterModel?.items?.length ?? 0}
            badgeColor={boardTheme}
          />

          {/* Custom Filters Popover */}
          <CustomFiltersModal
            isOpen={isCustomFiltersOpen}
            anchorEl={anchorEl}
            handleCloseCustomFilters={handleCloseCustomFilters}
          >
            <CustomStatusFilter
              options={filterOptions ?? ncoStatusFilters}
              setterFn={setStatusFilter ?? setNcoFilterStatus}
              filterModel={filterModel}
              value={statusFilter ?? ncoFilterStatus}
              onChange={handleSetStatusFilter}
              // context={statusKey}
              context={'masterStatus'}
              // label={statusLabel ?? 'No Label'}
            />
            <CustomStatusFilter
              options={SEO_FILTER_OPTIONS}
              setterFn={setOptType}
              filterModel={filterModel}
              value={optType}
              onChange={handleSetOptType}
              // context={statusKey}
              context={'type'}
              // label={'SEO Tier Filter'}
            />

            {/* <DateRangeFilterInput
              field="dateRange" // or "endDate" if you're filtering that
              filterModel={filterModel}
              setFilterModel={setFilterModel}
            /> */}
          </CustomFiltersModal>

          <FilterMyDataButton
            icon={
              <EntityAvatar
                name={profile?.name || ''}
                photoUrl={profile?.image_512}
                size="lg"
                showName={false}
                isLoading={isActiveUserLoading}
              />
            }
            filterModel={filterModel}
            onClick={handleFilterMyData}
            label={'My Items'}
          />
          {onClearWidths && <ResetColumnWidths columnWidths={columnWidths} onResetWidths={onClearWidths} />}
          <ResetGridColumns
            apiRef={apiRef}
            initialVisibilityState={initialVisibilityState}
            onClearVisibility={onClearVisibility}
          />
          <ResetSortingButton isSortModelChanged={isSortModelChanged} onClearSorting={onClearSorting} />
          <ResetFiltersButton filterModel={filterModel} handleClearFilters={handleClearFilters} />
          <Badge
            badgeContent={
              <>
                {filterModel &&
                filterModel.quickFilterValues &&
                Array.isArray(filterModel.quickFilterValues) &&
                filterModel.quickFilterValues.length > 0 ? (
                  <Typography
                    sx={{
                      fontSize: 12,
                      bgcolor: boardTheme,
                      p: 0.5,
                      height: '20px',
                      width: '20px',
                      borderRadius: '50%',
                      display: 'flex', // Flexbox centering
                      alignItems: 'center', // Vertically center
                      justifyContent: 'center', // Horizontally center
                      color: theme.palette.background.default,
                      fontWeight: 'bold',
                    }}
                  >
                    {filterModel && filterModel.quickFilterValues && Array.isArray(filterModel.quickFilterValues)
                      ? filterModel.quickFilterValues.length
                      : null}
                  </Typography>
                ) : null}
              </>
            }
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <QuickFilterComponent expanded={expanded} setExpanded={setExpanded} />
          </Badge>
        </Box>
      </Box>
    </Toolbar>
  )
}

export default OptimizationToolbar
